import { PlateInfoDTO, SearchDetailDTO, SearchItemsTarget } from 'business/models/Models';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';

export enum ESearchBarStatus {
  Empty,
  Loading,
  Finished,
  NoResults,
}

export enum EDatePreset {
  Any = 'Anytime',
  LastWeek = 'Last 7 Days',
  LastMonth = 'Last 30 Days',
  LastThreeMonths = 'Last 90 Days',
  Custom = 'Select Date',
}

export type SearchResult = {
  name: string;
  type: ETreeNodeTypes;
  nodeId: number;
  containerNodeId: number;
  entityId?: number;
  plates?: PlateInfoDTO[];
  status?: string;
  state?: string;
  datePrepared?: string;
  owner?: string;
};

export type RecentSearchResult = {
  id: number;
  searchDetail: SearchDetailDTO;
  searchDate: string;
};

export const SEARCH_BAR_TARGET_LABELS = {
  [SearchItemsTarget.Experiment]: 'Experiment',
  [SearchItemsTarget.Screen]: 'Screens',
  [SearchItemsTarget.Protein]: 'Protein',
  [SearchItemsTarget.FragmentScreen]: 'Fragment Screen',
  [SearchItemsTarget.Seed]: 'Seeds',
  [SearchItemsTarget.SynchrotronTripv4]: 'Synchrotron Trips',
  [SearchItemsTarget.SearchAndFilter]: 'Search and Filter',
};

export type SearchBarFilters = {
  query: string;
  fileName: string;
  ownerId?: number;
  targetTypes: SearchItemsTarget[];
  dateRange: { from: Date | null; to: Date | null };
  locationTreeNodeIds: number[];
  datePreset: EDatePreset;
};

export enum ESearchBarMode {
  Basic,
  Advanced,
}

export const DEFAULT_SEARCH_BAR_FILTERS: SearchBarFilters = {
  query: '',
  fileName: '',
  ownerId: null,
  targetTypes: [],
  dateRange: { from: null, to: null },
  locationTreeNodeIds: [],
  datePreset: EDatePreset.Custom,
};

export const getEffectiveSearchFilters = (
  filters: SearchBarFilters,
  mode: ESearchBarMode,
): SearchBarFilters => {
  if (mode === ESearchBarMode.Advanced) {
    return { ...filters, query: '' };
  }
  return {
    ...filters,
    fileName: '',
    ownerId: null,
    dateRange: { from: null, to: null },
    locationTreeNodeIds: [],
  };
};

export const toSearchDetailDTO = (
  filters: SearchBarFilters,
  limit?: number,
): SearchDetailDTO => {
  const searchDetail = new SearchDetailDTO();
  searchDetail.searchQuery = filters.query;
  searchDetail.fileName = filters.fileName;
  searchDetail.searchTarget = filters.targetTypes;
  searchDetail.ownerId = filters.ownerId ?? null;
  searchDetail.dateFrom = filters.dateRange?.from ?? null;
  searchDetail.dateTo = filters.dateRange?.to ?? null;
  searchDetail.locationTreeNodeIds =
    filters.locationTreeNodeIds.length > 0 ? filters.locationTreeNodeIds : null;
  searchDetail.searchLimit = limit;
  return searchDetail;
};

// Matches backend logic
export const getSearchIdentity = (searchDetail?: SearchDetailDTO): string => {
  const fileName = searchDetail?.fileName?.trim();
  if (fileName) {
    return fileName.toLowerCase();
  }
  return (searchDetail?.searchQuery ?? '').trim().toLowerCase();
};

// Also match backend, need to manually sync both
export const SEARCH_LIMIT = 5;
